import { inject, injectable } from 'inversify';
import { NotificationService, NotificationServiceSymbol } from '@/app/notification/service';
import { ErrorHandlingScorm2004Client, ErrorHandlingScorm2004ClientSymbol } from './error-handling-scorm2004-client'
import { TemporaryTrackingErrorNotification, FatalTrackingErrorNotification } from '../../../notification';
import { ScormError, CMIErrorCode } from '../api';
import { TemporaryErrorNotification } from '@xlearn/async-toolbox';
import { reportNonFatalError } from '@/app/error-handling';
import { createNotification } from '@/app/notification/api';
import { Subscription } from 'rxjs';
import { CompatibilityTrackingErrorNotification } from '@/app/tracking/notification/compatibility-tracking-error-notification';
import { ScormCommand } from './scorm-command';

export const ScormErrorNotificationServiceSymbol = Symbol.for('ScormErrorNotificationService')

@injectable()
export class ScormErrorNotificationService {

  private temporaryErrorNotification: TemporaryErrorNotification<ScormError, TemporaryTrackingErrorNotification>
  private nonTemporaryErrorSubscription?: Subscription
  private compatibilityErrorShown: boolean = false
  
  constructor(
    @inject(ErrorHandlingScorm2004ClientSymbol)
    private client: ErrorHandlingScorm2004Client,
    @inject(NotificationServiceSymbol)
    private notificationService: NotificationService) {

    this.temporaryErrorNotification = new TemporaryErrorNotification(notificationService, {
      debounceMs: 1500
    })
  }

  start() {
    this.compatibilityErrorShown = false
    this.enableNonTemporarySubscription()
    
    const input$ = this.client.notifications
    this.temporaryErrorNotification.enable(input$, {
      notificationId: 'temporary-tracking-error',
      notificationName: 'temporary-tracking-error',
      notificationFactory: (error) => {

        if (error instanceof Error) {
          reportNonFatalError(`Coding error: we don't expect this type of error here`, error)
          return null
        }

        const payload = this.formatError(error)
        return payload
      }
    })

  }

  stop() {
    this.temporaryErrorNotification.disable()
    this.disableNonTemporarySubscription()
  }

  private enableNonTemporarySubscription() {
    const input$ = this.client.notifications

    this.nonTemporaryErrorSubscription = input$.subscribe({
      next: (item) => {

        switch (item.type) {
          case 'fatal':
            this.handleFatal(item.error)
            break
          case 'skipped':
            this.handleSkipped(item.command, item.error)
            break
          default:
            break
        }
        
        if (item.type === 'fatal') {
        }
      }
    })
  }

  private handleSkipped(command: ScormCommand, error: ScormError | Error) {

    reportNonFatalError('error occurred while executing command', command, error)
    if (error instanceof Error) {
      return
    }
    
    switch (error.code) {
      case CMIErrorCode.generalArgumentError:
      case CMIErrorCode.undefinedDataModelElement:
      case CMIErrorCode.unimplementedDataModelElement:
      case CMIErrorCode.dataModelElementIsReadOnly:
      case CMIErrorCode.dataModelElementTypeMismatch:
      case CMIErrorCode.dataModelElementValueOutOfRange:
      case CMIErrorCode.dataModelDependencyNotEstablished:

        if (this.compatibilityErrorShown) {
          break
        }

        this.compatibilityErrorShown = true
        
        this.notificationService.show(createNotification<CompatibilityTrackingErrorNotification>(
          'compatibility-tracking-error',
          {
            title: 'Oktatási keretrendszer kompatibilitási probléma',
            message: 'A tananyag működik, de előfordulhat, hogy nem minden adat kerül elküldésre az oktatási keretrendszer felé.',
            technicalDetails: error instanceof Error ? error.toString() : formatStatus(error)
          },
          'compatibility-tracking-error',
          'pin', 'closeable'
        ))
    }
  }

  private handleFatal(error: ScormError | Error) {
    this.notificationService.show(createNotification<FatalTrackingErrorNotification>(
      'fatal-tracking-error',
      {
        title: 'Oktatási keretrendszer kapcsolati hiba',
        message: 'Kérjük, indítsa újra a tananyagot.',
        technicalDetails: error instanceof Error ? error.toString() : formatStatus(error)
      },
      'fatal-tracking-error',
      'pin', 'non-closeable'
    ))
  }

  private disableNonTemporarySubscription() {
    if (this.nonTemporaryErrorSubscription) {
      this.nonTemporaryErrorSubscription.unsubscribe()
      this.nonTemporaryErrorSubscription = undefined
    }
  }

  // TODO: language file
  private formatError(error: ScormError) {

    const restartMessage = 'Amennyiben ez az üzenet nem tűnik el pár másodpercen belül, kérjük, próbálja újraindítani a tananyagot.'

    switch (error.code) {
      case CMIErrorCode.generalException:
      case CMIErrorCode.generalInitializationFailure:
      case CMIErrorCode.generalTerminationFailure:
      case CMIErrorCode.generalGetFailure:
      case CMIErrorCode.generalSetFailure:
      case CMIErrorCode.generalCommitFailure:
        return {
          title: 'Átmeneti oktatási keretrendszer kapcsolati hiba',
          message: restartMessage,
          technicalDetails: formatStatus(error)
        }
      default:
        return {
          title: 'Nem várt oktatási keretrendszer kapcsolati hiba',
          message: restartMessage,
          technicalDetails: formatStatus(error)
        }
    }
  }
}

function formatStatus(error: ScormError) {
  return `${error.code}: ${error.message} (${error.diagnostic})`
}

